import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Award, Sparkles, PlayCircle, Flame, Home as HomeIcon, CheckCircle2 } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function HeroSection() {
  const { lang } = useLang();

  return (
    <section className="relative overflow-hidden wine-dark om-bg-dark pt-6 md:pt-10 pb-14">
      {/* Decorative subtle Om pattern */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Ctext x='0' y='55' font-size='55' fill='%23E9A756'%3Eॐ%3C/text%3E%3C/svg%3E")`
      }} />

      <div className="absolute top-16 -left-24 w-72 h-72 bg-nv-orange/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-24 w-96 h-96 bg-nv-gold/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left content */}
        <div className="space-y-5 md:space-y-6 fade-up order-2 lg:order-1">
          <span className="sanskrit-bar">
            || <span className="devnag">नवधा भक्ति, नवधा कृपा</span> ||
          </span>

          <h1 className="text-[2.15rem] sm:text-4xl md:text-5xl lg:text-[3.6rem] font-bold leading-[1.08] text-nv-cream">
            {lang === 'en' ? (
              <>
                <span className="block text-nv-cream">Aastha Aapki,</span>
                <span className="gradient-text-on-dark">Seva Hamari</span>
              </>
            ) : (
              <span className="devnag">
                <span className="block">आस्था आपकी,</span>
                <span className="gradient-text-on-dark">सेवा हमारी</span>
              </span>
            )}
          </h1>

          <p className="text-base md:text-lg text-nv-cream/80 max-w-xl leading-relaxed">
            {lang === 'en'
              ? 'Book online Pujas from home — performed by experienced Vedic Pandit Jis at Baba Baidyanath Dham (Deoghar), Kashi, Ujjain & other tirth kshetras with proper vidhi-vidhaan and shuddh mantras.'
              : 'घर बैठे कराएं सभी वैदिक पूजन, अनुभवी पंडितों द्वारा — पूर्ण विधि-विधान और शुद्ध मंत्रों के साथ बाबा धाम देवघर, काशी और उज्जैन में।'}
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/upcoming-pujas" className="btn-gold pulse-ring px-5 md:px-6 py-3 md:py-3.5 rounded-full font-semibold text-sm md:text-base flex items-center gap-2">
              {lang === 'en' ? 'Book Puja' : 'पूजा बुक करें'} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#how" onClick={(e) => { e.preventDefault(); document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="btn-outline-gold px-5 md:px-6 py-3 md:py-3.5 rounded-full font-semibold text-sm md:text-base flex items-center gap-2">
              <PlayCircle className="w-4 h-4" /> {lang === 'en' ? 'Know About Us' : 'हमारे बारे में जानें'}
            </a>
          </div>

          {/* Trust chips */}
          <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Award, label_en: 'Certified Pandits', label_hi: 'प्रमाणित पंडित', sub_en: 'Experienced & trusted', sub_hi: 'अनुभवी एवं विश्वसनीय' },
              { icon: Flame, label_en: 'Shuddh Vidhi', label_hi: 'शुद्ध विधि-विधान', sub_en: 'Per Vedic mantras', sub_hi: 'वैदिक मंत्रों के साथ' },
              { icon: Shield, label_en: 'Secure Payment', label_hi: 'सुरक्षित भुगतान', sub_en: '100% safe UPI', sub_hi: '100% सुरक्षित भुगतान' },
              { icon: HomeIcon, label_en: 'From Home', label_hi: 'घर बैठे सेवा', sub_en: 'Live video & Prasad', sub_hi: 'लाइव वीडियो + प्रसाद' }
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/5 border border-nv-gold/20 rounded-xl px-3 py-2 backdrop-blur-sm">
                <div className="w-9 h-9 rounded-lg bg-nv-gold/15 flex items-center justify-center text-nv-gold-light shrink-0">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs md:text-[13px] font-semibold text-nv-cream truncate">{lang === 'en' ? f.label_en : f.label_hi}</div>
                  <div className="text-[10px] md:text-[11px] text-nv-cream/60 truncate">{lang === 'en' ? f.sub_en : f.sub_hi}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="relative fade-up order-1 lg:order-2">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-nv-gold/30 ring-4 ring-nv-gold/10">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/0/05/Baidyanath_Dham.jpg"
              alt="Baba Baidyanath Dham, Deoghar"
              className="w-full h-[340px] sm:h-[400px] md:h-[480px] object-cover ken-burns"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-nv-wine-deep/85 via-nv-wine-deep/25 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 text-white">
              <div className="pill mb-2">🔥 {lang === 'en' ? 'Baba Baidyanath Dham • Deoghar' : 'बाबा बैद्यनाथ धाम • देवघर'}</div>
              <h3 className="text-lg md:text-2xl font-bold leading-tight drop-shadow-lg text-nv-gold-light">
                {lang === 'en' ? 'Har Har Mahadev' : 'हर हर महादेव'} 🔱
              </h3>
              <p className="text-xs md:text-sm opacity-90 mt-1">
                {lang === 'en' ? 'One of the 12 sacred Jyotirlingas of Bharat' : 'भारत के 12 पवित्र ज्योतिर्लिंगों में एक'}</p>
            </div>
          </div>

          {/* Floating badges */}
          <div className="hidden sm:flex absolute -top-4 -right-4 bg-nv-cream shadow-xl rounded-2xl p-3 border border-nv-gold/30 floating items-center gap-2">
            <div className="text-2xl">🪔</div>
            <div>
              <div className="text-[10px] text-nv-muted">{lang === 'en' ? 'Sacred Ritual' : 'पवित्र अनुष्ठान'}</div>
              <div className="text-sm font-bold text-nv-orange-deep">108 {lang === 'en' ? 'Mantras' : 'मंत्र'}</div>
            </div>
          </div>

          <div className="hidden md:flex absolute -bottom-5 -left-5 bg-nv-cream shadow-xl rounded-2xl p-3 border border-nv-gold/30 items-center gap-2 floating" style={{ animationDelay: '1s' }}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-nv-orange to-nv-orange-deep flex items-center justify-center text-white text-sm font-bold">4.9★</div>
            <div>
              <div className="text-[10px] text-nv-muted">{lang === 'en' ? 'Rated by' : 'मूल्यांकन'}</div>
              <div className="text-sm font-bold text-nv-wine">1 Lakh+ {lang === 'en' ? 'Devotees' : 'भक्त'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-10 md:mt-14 py-3 md:py-4 bg-nv-wine-deep border-y border-nv-gold/20 overflow-hidden">
        <div className="flex whitespace-nowrap marquee-inner">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-8 md:gap-12 pr-8 md:pr-12 text-xs md:text-sm font-medium text-nv-gold-light/85">
              <span>🙏 {lang === 'en' ? 'Trusted Vedic Pandits across Bharat' : 'भारत भर में विश्वसनीय वैदिक पंडित'}</span>
              <span className="text-nv-gold/50">◆</span>
              <span>🔥 {lang === 'en' ? 'Vedic Rituals' : 'वैदिक अनुष्ठान'}</span>
              <span className="text-nv-gold/50">◆</span>
              <span>🌼 {lang === 'en' ? 'Prasad Home Delivery' : 'प्रसाद घर तक डिलिवरी'}</span>
              <span className="text-nv-gold/50">◆</span>
              <span>📹 {lang === 'en' ? 'Live Video Recording' : 'लाइव वीडियो'}</span>
              <span className="text-nv-gold/50">◆</span>
              <span>✨ {lang === 'en' ? 'Based in Deoghar, Jharkhand' : 'देवघर, झारखंड से संचालित'}</span>
              <span className="text-nv-gold/50">◆</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
